import * as DateFns from "date-fns";
import { minutesToMilliseconds, isBefore as isBefore$1, isSameDay as isSameDay$1, isEqual, isAfter as isAfter$1, sub as sub$1, add as add$1, formatISO, parseISO, format as format$1 } from "date-fns";
import * as DateFnsTz from "date-fns-tz";
import { APIFieldType } from "@ardoq/api-types";
import { locales } from "@ardoq/locale";
import { clamp } from "lodash";
import libPluralize from "pluralize";
const normalizeToLocalDate = (date) => /^\s*\d{4}-\d{2}-\d{2}\s*$/.test(date) ? date.replace(/-/g, "/") : date;
const currentDate = () => /* @__PURE__ */ new Date();
const currentYear = () => (/* @__PURE__ */ new Date()).getFullYear();
const currentMonth = () => (/* @__PURE__ */ new Date()).getMonth();
const currentDateTimeISO = () => (/* @__PURE__ */ new Date()).toISOString();
const currentTimestamp = () => Date.now();
const currentUnixSeconds = () => Math.trunc(Date.now() / 1e3);
const copyDate = (date) => new Date(date);
const dateFromTimestamp = (timestamp) => new Date(timestamp);
const timestampToDateTimeISO = (timestamp) => new Date(timestamp).toISOString();
const parseDate = (date) => new Date(date);
const parseToTimestamp = (date) => Date.parse(date);
const parseToUnixSeconds = (date) => Math.trunc(Date.parse(date) / 1e3);
const constructDate = (...args) => new Date(...args);
const DEFAULT_TIME = "00:00:00.000";
const ISO8601StyleRegExp = /^\d{4}[/.-]\d{1,2}[/.-]\d{1,2}/i;
const localeAmbiguousDateRegExp = /^(\d{1,2})[/.,-]\s?(\d{1,2})[/.,-]\s?(\d{1,4})/i;
const timeRegExp = /\d{1,2}:\d{2}(:\d{2}(.\d{1,3})?)?(\s(AM|PM))?/i;
const MAX_YEAR = 2999;
const MIN_YEAR = 1946;
const MAX_MONTH = 12;
const MAX_DAY = 31;
const DATE_ERROR = "Invalid date";
const DATE_NOT_ALLOWED_ERR = "This date is not within the valid limits";
const isMDLocale = (locale) => locale.code === locales.enUS.code;
const getLocaleDayPosition = (locale) => isMDLocale(locale) ? 2 : 1;
const getLocaleMonthPosition = (locale) => isMDLocale(locale) ? 1 : 2;
const rangeCheckerMaker = (min2, max2) => (num) => num > min2 && num <= max2;
const isValidYearRange = rangeCheckerMaker(0, MAX_YEAR);
const isValidMonthRange = rangeCheckerMaker(0, MAX_MONTH);
const isValidDayRange = rangeCheckerMaker(0, MAX_DAY);
const getISO8601StringFromComponents = (year, month = "1", day = "1", time = DEFAULT_TIME) => {
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(
    2,
    "0"
  )}T${time}`;
};
const isPMTime = (timeString) => /PM/i.test(timeString);
const isAMTime = (timeString) => /AM/i.test(timeString);
const convertPMTimeToAbsolute = (timeString) => {
  const [hours, minutes, rest] = timeString.split(":");
  if (hours === void 0 || minutes === void 0) {
    return timeString;
  }
  const parsedHours = parseInt(hours, 10);
  const hoursAbsolute = parsedHours + (parsedHours < 12 ? 12 : 0);
  const hoursAndMinutes = `${hoursAbsolute}:${minutes}`;
  if (rest) {
    return `${hoursAndMinutes}:${rest}`;
  }
  return hoursAndMinutes;
};
const convertAMTimeToAbsolute = (timeString) => {
  const [hours, minutes, rest] = timeString.split(":");
  if (hours === void 0 || minutes === void 0) {
    return timeString;
  }
  const hoursWithMidnightFixed = hours === "12" ? `00` : hours;
  const hoursAndMinutes = `${hoursWithMidnightFixed}:${minutes}`;
  if (rest) {
    return `${hoursAndMinutes}:${rest}`;
  }
  return hoursAndMinutes;
};
const padHourIfNeeded = (timeString) => {
  if (timeString.split(":")[0].length === 1) {
    return `0${timeString}`;
  }
  return timeString;
};
const getCorrectedTime = (timeString) => {
  var _a;
  const time = ((_a = timeString.match(/\d{1,2}:\d{2}(:\d{2}(.\d{1,3})?)?/)) == null ? void 0 : _a[0]) ?? "";
  if (isPMTime(timeString)) {
    return convertPMTimeToAbsolute(time);
  } else if (isAMTime(timeString)) {
    return convertAMTimeToAbsolute(time);
  }
  return time;
};
const getTimeFromTimeString = (timeString = DEFAULT_TIME) => {
  const correctedTime = getCorrectedTime(timeString);
  return padHourIfNeeded(correctedTime);
};
const ardoqLocaleDateStringParser = (dateString, locale) => {
  var _a;
  const dateParts = dateString.match(localeAmbiguousDateRegExp);
  const time = getTimeFromTimeString((_a = dateString.match(timeRegExp)) == null ? void 0 : _a[0]);
  if (!dateParts || dateParts.length < 4) {
    return null;
  }
  const year = dateParts[3];
  const month = dateParts[getLocaleMonthPosition(locale)];
  const day = dateParts[getLocaleDayPosition(locale)];
  if (!isValidYearRange(parseInt(year, 10)) || !isValidMonthRange(parseInt(month, 10)) || !isValidDayRange(parseInt(day, 10))) {
    return null;
  }
  const dateISO = getISO8601StringFromComponents(year, month, day, time);
  const date = parseDate(dateISO);
  if (!isDateValid(date)) {
    return null;
  }
  return date;
};
const parseLocaleDateString = (inputString, locale) => {
  const dateString = inputString == null ? void 0 : inputString.trim();
  if (!dateString) return null;
  if (ISO8601StyleRegExp.test(dateString)) {
    const date2 = parseDate(normalizeToLocalDate(dateString));
    return isDateValid(date2) ? date2 : null;
  }
  if (localeAmbiguousDateRegExp.test(dateString)) {
    return ardoqLocaleDateStringParser(dateString, locale) ?? null;
  }
  const date = parseDate(normalizeToLocalDate(dateString));
  return isDateValid(date) ? date : null;
};
const isDateValid = (date) => {
  return !isNaN(date.getDate());
};
const isDateStringValid = (dateString, locale) => {
  const date = parseLocaleDateString(dateString, locale);
  if (!date) {
    return false;
  }
  return isDateValid(date);
};
const setYearWithinRange = (year) => {
  return clamp(Number(year), MIN_YEAR, MAX_YEAR);
};
const dateFromString = (date, locale) => {
  if (typeof date === "string") return parseLocaleDateString(date, locale);
  return date && isDateValid(date) ? date : null;
};
const isDateBefore = (a, b, locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);
  if (!aDate || !bDate) return false;
  return isBefore$1(aDate, bDate);
};
const isDateBeforeDay = (a, b, locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);
  if (!aDate || !bDate) return false;
  return isBefore$1(aDate, bDate) && !isSameDay$1(aDate, bDate);
};
const isDateSame = (a, b, locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);
  if (!aDate || !bDate) return false;
  return isEqual(aDate, bDate);
};
const isDateSameDay = (a, b, locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);
  if (!aDate || !bDate) return false;
  return isSameDay$1(aDate, bDate);
};
const isDateAfter = (a, b, locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);
  if (!aDate || !bDate) return false;
  return isAfter$1(aDate, bDate);
};
const isDateAfterDay = (a, b, locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);
  if (!aDate || !bDate) return false;
  return isAfter$1(aDate, bDate) && !isSameDay$1(aDate, bDate);
};
const methodHash = {
  "+": add$1,
  "-": sub$1
};
const unitHash = {
  h: "hours",
  d: "days",
  m: "months",
  y: "years"
};
const RELATIVE_DATE_REGEX = /^now([+-])?(\d+)?([hdmy])?/;
const isHourPrecision = (date) => {
  return RELATIVE_DATE_REGEX.test(date) && (date == null ? void 0 : date.slice(-1)) === "h";
};
const relativeDateCache = /* @__PURE__ */ new Map();
const CACHE_RESET_INTERVAL = minutesToMilliseconds(5);
let cacheStartTime = currentTimestamp();
const relativeDateToISOString = (relativeDate) => {
  if (currentTimestamp() - cacheStartTime > CACHE_RESET_INTERVAL) {
    relativeDateCache.clear();
    cacheStartTime = currentTimestamp();
  }
  const relativeDateMatch = relativeDate.match(RELATIVE_DATE_REGEX);
  if (!relativeDateMatch) return "Invalid date";
  if (relativeDate === "now") return currentDateTimeISO();
  const cacheHit = relativeDateCache.get(relativeDate);
  if (cacheHit) {
    return cacheHit;
  }
  const [, operator, count, unitSymbol] = relativeDateMatch;
  const method = methodHash[operator];
  const unit = unitHash[unitSymbol];
  const now = currentDate();
  if (typeof method !== "function") {
    throw new Error(
      `Unsupported operator encountered while parsing "${relativeDate}"`
    );
  }
  const newDate = method(now, { [unit]: +count });
  const isoString = newDate.toISOString();
  relativeDateCache.set(relativeDate, isoString);
  return isoString;
};
const formatDateTime = (date, locale) => {
  return formatDateByLocaleAndFormatPattern(date, "P p", locale);
};
const formatDateOnly = (date, locale) => {
  return formatDateByLocaleAndFormatPattern(date, "P", locale);
};
const formatDateNew = (date, locale) => {
  const formattedDate = formatDateByLocaleAndFormatPattern(date, "PP", locale);
  return formattedDate.replace(",", "");
};
const formatDateISO = (date) => {
  if (!date) return "";
  if (!isDateValid(date)) return "Invalid date";
  return formatISO(date, { representation: "date" });
};
const formatDateByLocaleAndFormatPattern = (dateOrDateString, dateFormat, locale) => {
  if (!dateOrDateString) return "";
  const date = dateFromString(dateOrDateString, locale);
  if (!date) return "Invalid date";
  return format$1(date, dateFormat, { locale });
};
const isISO8601DateOnly = (value) => formatDateISO(parseISO(value)) === value;
const isISO8601DateTime = (value) => {
  const date = parseDate(value);
  if (date.toString() === "Invalid Date") return false;
  return date.toISOString() === value;
};
const timeFormatCache = /* @__PURE__ */ new Map();
const isCurrentLocaleTimeFormat12Hour = (locale) => {
  if (timeFormatCache.has(locale)) return timeFormatCache.get(locale);
  const is12HourTime = new Intl.DateTimeFormat(locale.code).resolvedOptions().hour12 ?? false;
  timeFormatCache.set(locale, is12HourTime);
  return is12HourTime;
};
const isDateFieldType = (fieldType) => fieldType === APIFieldType.DATE_ONLY || fieldType === APIFieldType.DATE_TIME;
var TimeFrameUnit = /* @__PURE__ */ ((TimeFrameUnit2) => {
  TimeFrameUnit2["HOUR_AGO"] = "HOUR_AGO";
  TimeFrameUnit2["DAY_AGO"] = "DAY_AGO";
  TimeFrameUnit2["MONTH_AGO"] = "MONTH_AGO";
  TimeFrameUnit2["YEAR_AGO"] = "YEAR_AGO";
  TimeFrameUnit2["HOUR_FROM_NOW"] = "HOUR_FROM_NOW";
  TimeFrameUnit2["DAY_FROM_NOW"] = "DAY_FROM_NOW";
  TimeFrameUnit2["MONTH_FROM_NOW"] = "MONTH_FROM_NOW";
  TimeFrameUnit2["YEAR_FROM_NOW"] = "YEAR_FROM_NOW";
  return TimeFrameUnit2;
})(TimeFrameUnit || {});
const findUnit = (relativeDate) => {
  if (/now-\d+h/.test(relativeDate)) return TimeFrameUnit.HOUR_AGO;
  if (/now\+\d+h/.test(relativeDate)) return TimeFrameUnit.HOUR_FROM_NOW;
  if (/now-\d+d/.test(relativeDate)) return TimeFrameUnit.DAY_AGO;
  if (/now\+\d+d/.test(relativeDate)) return TimeFrameUnit.DAY_FROM_NOW;
  if (/now-\d+m/.test(relativeDate)) return TimeFrameUnit.MONTH_AGO;
  if (/now\+\d+m/.test(relativeDate)) return TimeFrameUnit.MONTH_FROM_NOW;
  if (/now-\d+y/.test(relativeDate)) return TimeFrameUnit.YEAR_AGO;
  if (/now\+\d+y/.test(relativeDate)) return TimeFrameUnit.YEAR_FROM_NOW;
  return null;
};
const dissectRelativeDate = (relativeDate) => {
  const unit = findUnit(relativeDate);
  const [, valueString] = relativeDate.match(/now[+-](\d+)[hdmy]/) || [];
  const value = parseInt(valueString, 10);
  if (!unit || !value) return null;
  return { unit, value };
};
const TODAY_OPTION = { value: "now+0d", label: "Today" };
const pluralize = (word, count) => libPluralize(word, count);
const relativeDateLabelFormatters = {
  [TimeFrameUnit.HOUR_AGO]: (count) => `${pluralize("Hour", count)} ago`,
  [TimeFrameUnit.DAY_AGO]: (count) => `${pluralize("Day", count)} ago`,
  [TimeFrameUnit.MONTH_AGO]: (count) => `${pluralize("Month", count)} ago`,
  [TimeFrameUnit.YEAR_AGO]: (count) => `${pluralize("Year", count)} ago`,
  [TimeFrameUnit.DAY_FROM_NOW]: (count) => `${pluralize("Day", count)} from now`,
  [TimeFrameUnit.MONTH_FROM_NOW]: (count) => `${pluralize("Month", count)} from now`,
  [TimeFrameUnit.YEAR_FROM_NOW]: (count) => `${pluralize("Year", count)} from now`,
  [TimeFrameUnit.HOUR_FROM_NOW]: (count) => `${pluralize("Hour", count)} from now`
};
const relativeDateDisplayLabel = (value) => {
  const dissectedDate = dissectRelativeDate(value);
  if (!dissectedDate) {
    return value === TODAY_OPTION.value ? TODAY_OPTION.label : false;
  }
  return `${dissectedDate.value} ${relativeDateLabelFormatters[dissectedDate.unit](dissectedDate.value)}`;
};
const {
  compareDesc,
  formatDistanceToNow,
  eachDayOfInterval,
  formatRelative,
  setSeconds,
  setMilliseconds,
  startOfToday,
  subDays,
  differenceInDays,
  differenceInSeconds,
  isToday,
  addHours,
  startOfHour,
  addDays,
  format,
  formatDuration,
  intervalToDuration,
  endOfDay,
  add,
  addQuarters,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMilliseconds,
  sub,
  endOfISOWeek,
  getISOWeek,
  getQuarter,
  getYear,
  startOfISOWeek,
  subQuarters,
  isBefore,
  isAfter,
  max,
  min,
  differenceInMinutes,
  isSameDay
  /**
   * We remove string parsing from date-fns functions in order to enforce usage
   * of our own parse functions to handle date vs dateTime correctly, which is
   * easy to mistake when date-fns is so flexible.
   */
} = DateFns;
const { getTimezoneOffset, formatInTimeZone, toZonedTime } = DateFnsTz;
export {
  DATE_ERROR,
  DATE_NOT_ALLOWED_ERR,
  MAX_YEAR,
  MIN_YEAR,
  RELATIVE_DATE_REGEX,
  TODAY_OPTION,
  TimeFrameUnit,
  add,
  addDays,
  addHours,
  addQuarters,
  compareDesc,
  constructDate,
  copyDate,
  currentDate,
  currentDateTimeISO,
  currentMonth,
  currentTimestamp,
  currentUnixSeconds,
  currentYear,
  dateFromTimestamp,
  differenceInDays,
  differenceInMinutes,
  differenceInSeconds,
  dissectRelativeDate,
  eachDayOfInterval,
  endOfDay,
  endOfISOWeek,
  format,
  formatDateISO,
  formatDateNew,
  formatDateOnly,
  formatDateTime,
  formatDistanceToNow,
  formatDuration,
  formatInTimeZone,
  formatRelative,
  getISOWeek,
  getQuarter,
  getTimezoneOffset,
  getYear,
  intervalToDuration,
  isAfter,
  isBefore,
  isCurrentLocaleTimeFormat12Hour,
  isDateAfter,
  isDateAfterDay,
  isDateBefore,
  isDateBeforeDay,
  isDateFieldType,
  isDateSame,
  isDateSameDay,
  isDateStringValid,
  isDateValid,
  isHourPrecision,
  isISO8601DateOnly,
  isISO8601DateTime,
  isSameDay,
  isToday,
  max,
  min,
  normalizeToLocalDate,
  parseDate,
  parseLocaleDateString,
  parseToTimestamp,
  parseToUnixSeconds,
  relativeDateDisplayLabel,
  relativeDateLabelFormatters,
  relativeDateToISOString,
  setMilliseconds,
  setSeconds,
  setYearWithinRange,
  startOfDay,
  startOfHour,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfToday,
  startOfWeek,
  startOfYear,
  sub,
  subDays,
  subMilliseconds,
  subQuarters,
  timestampToDateTimeISO,
  toZonedTime
};
//# sourceMappingURL=index.js.map
